/**
 * Twenty-Nine — server-authoritative rules engine (South Asian partnership trick-taking, 4 players).
 *
 * Rules implemented (toggles live in {@link TwentyNineConfig}):
 *  - 32-card deck (7 8 9 10 J Q K A). Seats 0 & 2 (team 0) play seats 1 & 3 (team 1).
 *  - Trick order in every suit is J, 9, A, 10, K, Q, 8, 7. Card points: J 3, 9 2, A 1, 10 1, others 0 — 28 in all.
 *  - Four cards are dealt to each player, then one round of bidding (15..28 points) starts left of the dealer. The
 *    dealer must bid the minimum when the others all pass.
 *  - The highest bidder secretly picks the trump suit, then the remaining four cards are dealt to each player.
 *  - The player left of the dealer leads. Players must follow the led suit. A player who cannot follow may "ask for
 *    the trump": the hidden suit is revealed to everyone and from then on trumps are live. Before that, a void player
 *    may only throw a card that is not of the hidden trump suit. Trumps win a trick once revealed.
 *  - The bidding team makes its bid when the card points of its tricks reach the bid: +1 game point, else −1. The
 *    defenders never score. The match ends when a team reaches +targetScore (it wins) or −targetScore (it loses).
 *    Partners share a place, so payouts are split between the two partners.
 */
import { makeCardId, parseCard, RANKS, removeCards, sortHand, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { checkBool, checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface TwentyNineConfig {
  /** A team reaching +target wins; reaching −target loses. */
  targetScore: number;
  /** Hard cap on rounds so a match always terminates. */
  maxRounds: number;
  minBid: number;
  /** When everyone else passed, the dealer must bid. */
  dealerMustBid: boolean;
  roundEndDelayMs: number;
  trickEndDelayMs: number;
}

export type TwentyNinePhase = 'BIDDING' | 'TRUMP' | 'PLAYING' | 'TRICK_END' | 'ROUND_END' | 'MATCH_END';

export interface TwentyNineTrickPlay {
  seat: number;
  card: CardId;
}

export interface TwentyNineCompletedTrick {
  leader: number;
  plays: TwentyNineTrickPlay[];
  winner: number;
  points: number;
}

export interface TwentyNineTeamRound {
  declarer: boolean;
  bid: number;
  /** Card points the team's tricks were worth. */
  points: number;
  /** Game points gained or lost. */
  delta: number;
}

export interface TwentyNineState {
  config: TwentyNineConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: TwentyNinePhase;
  hands: CardId[][];
  /** Cards still to be dealt once the trump is picked (four per seat). */
  stock: CardId[];
  bids: (number | null)[];
  highBid: { seat: number; value: number } | null;
  trump: Suit | null;
  trumpRevealed: boolean;
  /** Seat that asked for the trump, and the 1-based trick it happened in. */
  revealedBy: number | null;
  revealTrick: number | null;
  tricksWon: number[];
  /** Card points won per team this round. */
  points: number[];
  turn: number;
  trick: TwentyNineTrickPlay[];
  tricks: TwentyNineCompletedTrick[];
  teamScores: number[];
  roundResults: TwentyNineTeamRound[][];
}

export type TwentyNineAction =
  | { type: 'bid'; value: number }
  | { type: 'pass' }
  | { type: 'trump'; suit: Suit }
  | { type: 'reveal' }
  | { type: 'play'; card: CardId };

export type TwentyNineLegal =
  | { kind: 'bid'; min: number; max: number; canPass: boolean }
  | { kind: 'trump'; suits: Suit[] }
  | { kind: 'play'; cards: CardId[]; canReveal: boolean }
  | { kind: 'none' };

export interface TwentyNineView {
  gameType: 'TWENTY_NINE';
  config: TwentyNineConfig;
  seat: number | null;
  phase: TwentyNinePhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  bids: (number | null)[];
  highBid: { seat: number; value: number } | null;
  declarer: number | null;
  /** Only the bidder sees the trump until it is revealed. */
  trump: Suit | null;
  trumpSet: boolean;
  trumpRevealed: boolean;
  revealedBy: number | null;
  tricksWon: number[];
  points: number[];
  trick: TwentyNineTrickPlay[];
  lastTrick: TwentyNineCompletedTrick | null;
  tricks: TwentyNineCompletedTrick[];
  teamScores: number[];
  roundResults: TwentyNineTeamRound[][];
  totals: number[];
}

const DEFAULTS: TwentyNineConfig = {
  targetScore: 6,
  maxRounds: 20,
  minBid: 15,
  dealerMustBid: true,
  roundEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
export const TWENTY_NINE_MAX_BID = 28;

export const twentyNineTeamOf = (seat: number): number => seat % 2;
export const twentyNinePartnerOf = (seat: number): number => (seat + 2) % SEATS;

const STRENGTH: Record<number, number> = { 11: 8, 9: 7, 14: 6, 10: 5, 13: 4, 12: 3, 8: 2, 7: 1 };
const POINTS: Record<number, number> = { 11: 3, 9: 2, 14: 1, 10: 1 };

/** Trick strength inside a suit: J 8, 9 7, A 6, 10 5, K 4, Q 3, 8 2, 7 1. */
export const twentyNineStrength = (card: CardId): number => STRENGTH[parseCard(card).rank]!;
/** Card points: J 3, 9 2, A 1, 10 1. */
export const twentyNinePoints = (card: CardId): number => POINTS[parseCard(card).rank] ?? 0;

export function twentyNineDeck(): CardId[] {
  const out: CardId[] = [];
  for (const suit of SUITS) for (const rank of RANKS) if (rank >= 7) out.push(makeCardId(rank, suit));
  return out;
}

/** Winner of a complete or partial trick. `trump` is null while the trump is hidden (it then has no special power). */
export function twentyNineTrickWinner(plays: readonly TwentyNineTrickPlay[], trump: Suit | null): number {
  const lead = parseCard(plays[0]!.card).suit;
  const trumps = trump ? plays.filter((p) => parseCard(p.card).suit === trump) : [];
  const pool = trumps.length > 0 ? trumps : plays.filter((p) => parseCard(p.card).suit === lead);
  return pool.reduce((best, p) => (twentyNineStrength(p.card) > twentyNineStrength(best.card) ? p : best)).seat;
}

export function twentyNineTrickPoints(plays: readonly TwentyNineTrickPlay[]): number {
  return plays.reduce((n, p) => n + twentyNinePoints(p.card), 0);
}

/** Cards a seat may play now, and whether it may (or, with nothing playable, must) ask for the trump. */
export function twentyNineLegalPlay(
  hand: readonly CardId[],
  trick: readonly TwentyNineTrickPlay[],
  trump: Suit | null,
  revealed: boolean,
): { cards: CardId[]; canReveal: boolean } {
  if (trick.length === 0) return { cards: [...hand], canReveal: false };
  const lead = parseCard(trick[0]!.card).suit;
  const followers = hand.filter((c) => parseCard(c).suit === lead);
  if (followers.length > 0) return { cards: followers, canReveal: false };
  if (revealed || trump === null) return { cards: [...hand], canReveal: false };
  return { cards: hand.filter((c) => parseCard(c).suit !== trump), canReveal: true };
}

export function twentyNineBidRange(state: Pick<TwentyNineState, 'config' | 'highBid' | 'dealer'>, seat: number): { min: number; max: number; canPass: boolean } {
  const min = Math.max(state.config.minBid, (state.highBid?.value ?? 0) + 1);
  const forced = state.config.dealerMustBid && seat === state.dealer && state.highBid === null;
  return { min, max: TWENTY_NINE_MAX_BID, canPass: !forced };
}

/** Suit with the most (and strongest) cards — the default trump pick. */
export function twentyNineBestSuit(hand: readonly CardId[]): Suit {
  let best: Suit = SUITS[0]!;
  let bestScore = -1;
  for (const suit of SUITS) {
    const cards = hand.filter((c) => parseCard(c).suit === suit);
    const score = cards.length * 10 + cards.reduce((n, c) => n + twentyNineStrength(c), 0);
    if (score > bestScore) {
      bestScore = score;
      best = suit;
    }
  }
  return best;
}

function deal(state: TwentyNineState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(twentyNineDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  deck.slice(0, 16).forEach((c, i) => hands[i % SEATS]!.push(c));
  state.hands = hands.map((h) => sortHand(h));
  state.stock = deck.slice(16);
  state.bids = [null, null, null, null];
  state.highBid = null;
  state.trump = null;
  state.trumpRevealed = false;
  state.revealedBy = null;
  state.revealTrick = null;
  state.tricksWon = [0, 0, 0, 0];
  state.points = [0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'BIDDING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, counts: state.hands.map((h) => h.length) });
}

export const twentyNineEngine: GameEngine<TwentyNineConfig, TwentyNineState, TwentyNineAction, TwentyNineView, TwentyNineLegal> = {
  gameType: 'TWENTY_NINE',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 2, 30);
    checkInt('maxRounds', c.maxRounds, 1, 60);
    checkInt('minBid', c.minBid, 15, 28);
    checkBool('dealerMustBid', c.dealerMustBid);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Twenty-Nine needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: TwentyNineState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'BIDDING',
      hands: [],
      stock: [],
      bids: [],
      highBid: null,
      trump: null,
      trumpRevealed: false,
      revealedBy: null,
      revealTrick: null,
      tricksWon: [],
      points: [0, 0],
      turn: 0,
      trick: [],
      tricks: [],
      teamScores: [0, 0],
      roundResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'bid' && typeof raw.value === 'number' && Number.isInteger(raw.value)) return { type: 'bid', value: raw.value };
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'reveal') return { type: 'reveal' };
    if (raw.type === 'trump' && typeof raw.suit === 'string' && (SUITS as readonly string[]).includes(raw.suit)) return { type: 'trump', suit: raw.suit as Suit };
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Twenty-Nine action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && (state.phase === 'BIDDING' || state.phase === 'TRUMP' || state.phase === 'PLAYING'), 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (action.type === 'bid' || action.type === 'pass') {
      assertEngine(state.phase === 'BIDDING', 'INVALID_PHASE', 'Bidding is closed');
      const range = twentyNineBidRange(state, seat);
      if (action.type === 'pass') {
        assertEngine(range.canPass, 'ILLEGAL_MOVE', 'The dealer must bid when everyone else passed');
        state.bids[seat] = 0;
        events.push({ type: 'pass', seat });
      } else {
        assertEngine(action.value >= range.min && action.value <= range.max, 'INVALID_AMOUNT', 'Bid out of range');
        state.bids[seat] = action.value;
        state.highBid = { seat, value: action.value };
        events.push({ type: 'bid', seat, value: action.value });
      }
      if (state.bids.every((b) => b !== null)) {
        const winner = state.highBid!;
        state.phase = 'TRUMP';
        state.turn = winner.seat;
        events.push({ type: 'biddingComplete', declarer: winner.seat, bid: winner.value });
      } else {
        state.turn = (seat + 1) % SEATS;
      }
      return events;
    }

    if (action.type === 'trump') {
      assertEngine(state.phase === 'TRUMP', 'INVALID_PHASE', 'Trump is not being chosen');
      state.trump = action.suit;
      // The rest of the deck is dealt only once the trump is set.
      for (let s = 0; s < SEATS; s++) state.hands[s] = sortHand([...state.hands[s]!, ...state.stock.slice(s * 4, s * 4 + 4)]);
      state.stock = [];
      state.phase = 'PLAYING';
      state.turn = (state.dealer + 1) % SEATS;
      events.push({ type: 'trumpChosen', seat });
      return events;
    }

    assertEngine(state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const hand = state.hands[seat]!;
    const legal = twentyNineLegalPlay(hand, state.trick, state.trump, state.trumpRevealed);

    if (action.type === 'reveal') {
      assertEngine(legal.canReveal, 'ILLEGAL_MOVE', 'You cannot ask for the trump now');
      state.trumpRevealed = true;
      state.revealedBy = seat;
      state.revealTrick = state.tricks.length + 1;
      events.push({ type: 'trumpRevealed', seat, suit: state.trump });
      return events;
    }

    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(legal.cards.includes(action.card), 'ILLEGAL_MOVE', 'That card is not allowed now');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = twentyNineTrickWinner(state.trick, state.trumpRevealed ? state.trump : null);
    const points = twentyNineTrickPoints(state.trick);
    state.tricksWon[winner]!++;
    state.points[twentyNineTeamOf(winner)]! += points;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner, points });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length, points });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'BIDDING' || state.phase === 'TRUMP' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'BIDDING') return { kind: 'bid', ...twentyNineBidRange(state, seat) };
    if (state.phase === 'TRUMP') return { kind: 'trump', suits: [...SUITS] };
    if (state.phase === 'PLAYING') return { kind: 'play', ...twentyNineLegalPlay(state.hands[seat]!, state.trick, state.trump, state.trumpRevealed) };
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'BIDDING') {
      const range = twentyNineBidRange(state, seat);
      return range.canPass ? { type: 'pass' } : { type: 'bid', value: range.min };
    }
    if (state.phase === 'TRUMP') return { type: 'trump', suit: twentyNineBestSuit(state.hands[seat]!) };
    const legal = twentyNineLegalPlay(state.hands[seat]!, state.trick, state.trump, state.trumpRevealed);
    if (legal.cards.length === 0) return { type: 'reveal' };
    const lowest = [...legal.cards].sort((a, b) => twentyNinePoints(a) - twentyNinePoints(b) || twentyNineStrength(a) - twentyNineStrength(b))[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    const knowsTrump = state.trumpRevealed || (seat !== null && state.highBid?.seat === seat && state.trump !== null);
    return {
      gameType: 'TWENTY_NINE',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      bids: [...state.bids],
      highBid: state.highBid ? { ...state.highBid } : null,
      declarer: state.phase === 'BIDDING' ? null : (state.highBid?.seat ?? null),
      trump: knowsTrump ? state.trump : null,
      trumpSet: state.trump !== null,
      trumpRevealed: state.trumpRevealed,
      revealedBy: state.revealedBy,
      tricksWon: [...state.tricksWon],
      points: [...state.points],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      roundResults: state.roundResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[twentyNineTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < 8) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const cfg = state.config;
      const { seat: declarerSeat, value: bid } = state.highBid!;
      const declarerTeam = twentyNineTeamOf(declarerSeat);
      const made = state.points[declarerTeam]! >= bid;
      const results: TwentyNineTeamRound[] = [0, 1].map((team) => ({
        declarer: team === declarerTeam,
        bid,
        points: state.points[team]!,
        delta: team === declarerTeam ? (made ? 1 : -1) : 0,
      }));
      results.forEach((r, team) => {
        state.teamScores[team]! += r.delta;
      });
      state.roundResults.push(results);
      events.push({ type: 'roundEnd', round: state.round, results, teamScores: [...state.teamScores], made });
      const [a, b] = state.teamScores as [number, number];
      const decided = Math.abs(a) >= cfg.targetScore || Math.abs(b) >= cfg.targetScore;
      if ((decided && a !== b) || state.round >= cfg.maxRounds) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'ROUND_END';
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};

/** Partners share a place: the higher-scoring team is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: TwentyNineState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = twentyNineTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}
